import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Outlet, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CsrAPI } from "../apis/CsrAPI";
import {
  fetchScsTflContent,
  resetScsForSummary,
} from "../store/slices/scsDataSlice";
import {
  CSR_STATUS,
  FILE_UPLOAD_STATUS,
  DOCUMENT_TYPE,
  SCS_DOCUMENT_TYPE,
  FILES_STATUS,
  LS_STATUS,
} from "../utils/Constants";
import FormatDate from "../utils/FormatDate";
import { saveAs } from "file-saver";
export const CsrContext = createContext(null);

let updateFilesStatus;
let updateScsFilesStatus;

export default function CsrProvider() {
  const { studyId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const statusPollingRef = useRef(null);
  const errRef = useRef();

  const [ajaxLoading, setAjaxLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [wrongEnvironment, setWrongEnvironment] = useState(false);

  const [currentStudyName, setCurrentStudyName] = useState("");
  const [currentLaySummaryName, setCurrentLaySummaryName] = useState("");

  const [currentRequestId, setCurrentRequestId] = useState("");
  const [mainStatus, setMainStatus] = useState(CSR_STATUS.inProgress);
  const [currentRequestTime, setCurrentRequestTime] = useState("");

  const [currentRequestIdOfLaySummary, setCurrentRequestIdOfLaySummary] =
    useState("");
  const [mainStatusOfLaySummary, setMainStatusOfLaySummary] = useState(
    CSR_STATUS.inProgress
  );
  const [currentRequestTimeOfLaySummary, setCurrentRequestTimeOfLaySummary] =
    useState("");

  const [currentTrafficLights, setCurrentTrafficLights] = useState([]);

  const cspProgressRef = useRef(null);
  const sapProgressRef = useRef(null);
  const tflProgressRef = useRef(null);
  const [cspProgress, setCspProgress] = useState({ val: [] });
  const [sapProgress, setSapProgress] = useState({ val: [] });
  const [tflProgress, setTflProgress] = useState({ val: [] });

  const [documentsList, setDocumentsList] = useState({
    csp: [],
    sap: [],
    tfl: [],
  });

  const [filesStatus, setFilesStatus] = useState(FILES_STATUS.ready);
  const [questionsStatus, setQuestionsStatus] = useState(true);
  const [selectedStudy, setSelectedStudy] = useState("");

  const gidsProgressRef = useRef(null);
  const sdapProgressRef = useRef(null);
  const csrsProgressRef = useRef(null);
  const isoProgressRef = useRef(null);
  const [gidsProgress, setGidsProgress] = useState({ val: [] });
  const [sdapProgress, setSdapProgress] = useState({ val: [] });
  const [csrsProgress, setCsrsProgress] = useState({ val: [] });
  const [isoProgress, setIsoProgress] = useState({ val: [] });

  const [scsDocumentsList, setScsDocumentsList] = useState({
    gids: [],
    sdap: [],
    csrs: [],
    iso: [],
  });
  const [scsFilesStatus, setScsFilesStatus] = useState(FILES_STATUS.ready);

  const stopStatusPolling = useCallback(() => {
    if (statusPollingRef.current) {
      clearTimeout(statusPollingRef.current);
      statusPollingRef.current = null;
    }
  }, []);

  const getStudyInfo = useCallback(studyId => {
    CsrAPI.getOneStudy({ studyId })
      .then(studyInfo => {
        setCurrentStudyName(studyInfo.name);

        if (studyInfo.latest_request_status === "render available") {
          setCurrentRequestId(studyInfo.latest_request_id);
          setMainStatus(CSR_STATUS.ready);
        }

        if (studyInfo.latest_request_id)
          getCsrStatus(studyInfo.study_id, studyInfo.latest_request_id);
      })
      .catch(error => {
        if (error.response?.status === 403) {
          setWrongEnvironment(true);
        } else {
          setErrMsg("No Server Response");
        }
      });
  }, []);

  const getLaySummaryInfo = useCallback(lay_summary_id => {
    CsrAPI.getLatestLaySummaryRequest({ lay_summary_id })
      .then(studyInfo => {
        setCurrentRequestIdOfLaySummary(studyInfo.request.request_id);
        setCurrentRequestTimeOfLaySummary(
          FormatDate(studyInfo.request.created_at)
        );
        setMainStatusOfLaySummary(studyInfo.request.request_status);

        if (studyInfo.request.request_id)
          getLaySummaryStatus(
            studyInfo.request.lay_summary_id,
            studyInfo.request.request_id
          );
      })
      .catch(error => {
        if (error.response?.status === 403) {
          setWrongEnvironment(true);
        } else if (error.response?.status === 404) {
          setMainStatusOfLaySummary(LS_STATUS.readyToStart);
        } else {
          // setErrMsg("Request details not found!");//TODO - BE needs to handle 404 for request id not found
        }
      });
  }, []);

  const getLaySummaryStatus = useCallback(
    (lay_summary_id, requestId) => {
      stopStatusPolling();
      CsrAPI.getLaySummaryStatus({ lay_summary_id, requestId })
        .then(result => {
          switch (result.request_status) {
            case "PENDING":
              setMainStatusOfLaySummary(LS_STATUS.generating);
              setAjaxLoading(true);
              statusPollingRef.current = setTimeout(() => {
                getLaySummaryStatus(lay_summary_id, requestId);
              }, 30000);
              break;
            case "AI_SUMMARY_COMPLETED":
              setMainStatusOfLaySummary(LS_STATUS.aiSummaryReady);
              setAjaxLoading(true);
              setErrMsg("");
              statusPollingRef.current = setTimeout(() => {
                getLaySummaryStatus(lay_summary_id, requestId);
              }, 30000);
              break;
            case "COMPLETED":
              setMainStatusOfLaySummary(LS_STATUS.ready);
              setAjaxLoading(false);
              setErrMsg("");
              break;
            case "COMPLETED_WITH_ERRORS":
              setMainStatusOfLaySummary(LS_STATUS.errored);
              setAjaxLoading(false);
              break;
            default:
              console.warn(`Unexpected CSR status: ${result.status}`);
              setAjaxLoading(false);
              break;
          }
        })
        .catch(error => {
          if (error.response?.status === 403) {
            setWrongEnvironment(true);
          }
        });
    },
    [stopStatusPolling]
  );

  const getCsrStatus = useCallback(
    (studyId, requestId) => {
      stopStatusPolling();
      setCurrentTrafficLights([]);
      setCurrentRequestId(requestId);

      CsrAPI.getCsrStatus({ studyId, requestId })
        .then(result => {
          switch (result.status) {
            case "stopped":
              setMainStatus(CSR_STATUS.inProgress);
              break;
            case "ready":
            case "finished":
              setMainStatus(CSR_STATUS.generating);
              statusPollingRef.current = setTimeout(() => {
                getCsrStatus(studyId, requestId);
              }, 3000);
              break;
            case "render available": {
              const formattedDate = FormatDate(result.finished_at);
              setCurrentRequestTime(formattedDate);
              setMainStatus(CSR_STATUS.ready);
              setCurrentTrafficLights(result.message);
              break;
            }
            case "error":
              setMainStatus(CSR_STATUS.errored);
              setErrMsg(CSR_STATUS.errored);
              break;
            default:
              console.warn(`Unexpected CSR status: ${result.status}`);
              break;
          }
        })
        .catch(error => {
          if (error.response?.status === 403) {
            setWrongEnvironment(true);
          }
        });
    },
    [stopStatusPolling]
  );

  const getDocuments = useCallback((studyId, loadingState = "subsequent") => {
    if (loadingState === "initial") setAjaxLoading(true);

    CsrAPI.getDocuments({ studyId })
      .then(result => {
        let cspArray = [];
        let sapArray = [];
        let tflArray = [];
        let allFilesStatuses = FILES_STATUS.readyEmpty;

        result?.forEach(doc => {
          if (doc.document_type === DOCUMENT_TYPE.csp) {
            cspArray = [...cspArray, doc];
          } else if (doc.document_type === DOCUMENT_TYPE.sap) {
            sapArray = [...sapArray, doc];
          } else if (doc.document_type === DOCUMENT_TYPE.tfl) {
            tflArray = [...tflArray, doc];
          }

          if (doc.status === FILE_UPLOAD_STATUS.errored) {
            allFilesStatuses = FILES_STATUS.warning;
          }

          if (allFilesStatuses !== FILES_STATUS.warning) {
            if (doc.status === FILE_UPLOAD_STATUS.uploaded) {
              allFilesStatuses = FILES_STATUS.pending;
            }

            if (allFilesStatuses !== FILES_STATUS.pending)
              allFilesStatuses = FILES_STATUS.ready;
          }
        });

        setDocumentsList({ csp: cspArray, sap: sapArray, tfl: tflArray });

        setFilesStatus(allFilesStatuses);
      })
      .catch(error => {
        if (error.response?.status === 403) {
          setWrongEnvironment(true);
        } else {
          setFilesStatus(FILES_STATUS.warning);
        }
      })
      .finally(() => {
        setAjaxLoading(false);
      });
  }, []);

  const getSafetyDocuments = useCallback(
    (summaryId, loadingState = "subsequent") => {
      if (loadingState === "initial") setAjaxLoading(true);

      CsrAPI.getSafetySummaryDocuments({ summaryId })
        .then(result => {
          let gidsArray = [];
          let sdapArray = [];
          let csrsArray = [];
          let isoArray = [];
          let allFilesStatuses = FILES_STATUS.readyEmpty;

          result?.forEach(doc => {
            if (doc.document_type === SCS_DOCUMENT_TYPE.gids) {
              gidsArray = [...gidsArray, doc];
            } else if (doc.document_type === SCS_DOCUMENT_TYPE.sdap) {
              sdapArray = [...sdapArray, doc];
            } else if (doc.document_type === SCS_DOCUMENT_TYPE.csrs) {
              csrsArray = [...csrsArray, doc];
            } else if (doc.document_type === SCS_DOCUMENT_TYPE.iso) {
              isoArray = [...isoArray, doc];
            }

            if (doc.status === FILE_UPLOAD_STATUS.errored) {
              allFilesStatuses = FILES_STATUS.warning;
            }

            if (allFilesStatuses !== FILES_STATUS.warning) {
              if (doc.status === FILE_UPLOAD_STATUS.uploaded) {
                allFilesStatuses = FILES_STATUS.pending;
              }

              if (allFilesStatuses !== FILES_STATUS.pending)
                allFilesStatuses = FILES_STATUS.ready;
            }
          });

          setScsDocumentsList({
            gids: gidsArray,
            sdap: sdapArray,
            csrs: csrsArray,
            iso: isoArray,
          });

          setScsFilesStatus(allFilesStatuses);

          // Once every uploaded file is PROCESSED (ready, and not the
          // no-files readyEmpty case) the polling loop stops and the TFL
          // content becomes available — pull it into the dedicated scsData
          // branch of the redux store. The thunk's condition guard dedupes,
          // so repeated ready polls / re-mounts won't re-request it.
          if (allFilesStatuses === FILES_STATUS.ready) {
            dispatch(fetchScsTflContent(summaryId));
          }
        })
        .catch(error => {
          if (error.response?.status === 403) {
            setWrongEnvironment(true);
          } else {
            setScsFilesStatus(FILES_STATUS.warning);
          }
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    },
    [dispatch]
  );

  const downloadLatestFile = useCallback(
    (studyId, currentStudyName, fileURL) => {
      setAjaxLoading(true);
      CsrAPI.downloadLatestFile({ studyId, fileURL })
        .then(blob => {
          setErrMsg("");
          saveAs(blob, currentStudyName);
        })
        .catch(error => {
          console.error(error);

          const status =
            error.response?.status || error.originalError?.response?.status;

          if (status === 403 && !error.isPresignedUrlError) {
            setWrongEnvironment(true);
          } else if (!error.response && !error.originalError?.response) {
            setErrMsg("No Server Response");
          } else if (status === 401) {
            setErrMsg("Invalid user credentials");
          } else {
            setErrMsg("Failed to download File");
          }
          errRef.current?.focus();
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    stopStatusPolling();
    if (updateFilesStatus) {
      clearInterval(updateFilesStatus);
      updateFilesStatus = null;
    }
    if (updateScsFilesStatus) {
      clearInterval(updateScsFilesStatus);
      updateScsFilesStatus = null;
    }

    if (studyId) {
      setErrMsg("");
      setDocumentsList({
        csp: [],
        sap: [],
        tfl: [],
      });
      setScsDocumentsList({
        gids: [],
        sdap: [],
        csrs: [],
        iso: [],
      });

      const app = location.pathname.split("/")[1];
      if (app === "scs") {
        // Bind the SCS slice to this summary before any TFL fetch so the
        // slice's summary-scoped guards accept the incoming data.
        dispatch(resetScsForSummary(studyId));
        getSafetyDocuments(studyId, "initial");
      } else if (app !== "ls") {
        getStudyInfo(studyId);
        getDocuments(studyId, "initial");
      }
    }

    return () => {
      stopStatusPolling();
      if (updateFilesStatus) {
        clearInterval(updateFilesStatus);
        updateFilesStatus = null;
      }
      if (updateScsFilesStatus) {
        clearInterval(updateScsFilesStatus);
        updateScsFilesStatus = null;
      }
    };
  }, [studyId, stopStatusPolling]);

  useEffect(() => {
    if (studyId) {
      if (filesStatus === FILES_STATUS.pending) {
        updateFilesStatus = setInterval(() => {
          getDocuments(studyId);
        }, 10000);
      } else {
        if (updateFilesStatus) {
          clearInterval(updateFilesStatus);
        }
      }
    }
  }, [filesStatus]);

  useEffect(() => {
    if (studyId) {
      if (scsFilesStatus === FILES_STATUS.pending) {
        updateScsFilesStatus = setInterval(() => {
          getSafetyDocuments(studyId);
        }, 10000);
      } else {
        if (updateScsFilesStatus) {
          clearInterval(updateScsFilesStatus);
          updateScsFilesStatus = null;
        }
      }
    }
  }, [scsFilesStatus]);

  return (
    <CsrContext.Provider
      value={{
        ajaxLoading,
        setAjaxLoading,
        errRef,
        errMsg,
        setErrMsg,
        getStudyInfo,
        getLaySummaryInfo,
        getDocuments,
        downloadLatestFile,
        getCsrStatus,
        getLaySummaryStatus,
        stopStatusPolling,
        currentStudyName,
        currentLaySummaryName,
        setCurrentStudyName,
        setCurrentLaySummaryName,
        currentRequestId,
        setCurrentRequestId,
        currentRequestTime,
        setCurrentRequestTime,
        currentTrafficLights,
        setCurrentTrafficLights,
        cspProgressRef,
        sapProgressRef,
        tflProgressRef,
        cspProgress,
        setCspProgress,
        sapProgress,
        setSapProgress,
        tflProgress,
        setTflProgress,
        documentsList,
        setDocumentsList,
        mainStatus,
        setMainStatus,
        filesStatus,
        setFilesStatus,
        questionsStatus,
        setQuestionsStatus,
        wrongEnvironment,
        currentRequestIdOfLaySummary,
        setCurrentRequestIdOfLaySummary,
        mainStatusOfLaySummary,
        setMainStatusOfLaySummary,
        currentRequestTimeOfLaySummary,
        setCurrentRequestTimeOfLaySummary,
        selectedStudy,
        setSelectedStudy,
        getSafetyDocuments,
        scsDocumentsList,
        setScsDocumentsList,
        scsFilesStatus,
        setScsFilesStatus,
        gidsProgressRef,
        sdapProgressRef,
        csrsProgressRef,
        isoProgressRef,
        gidsProgress,
        setGidsProgress,
        sdapProgress,
        setSdapProgress,
        csrsProgress,
        setCsrsProgress,
        isoProgress,
        setIsoProgress,
      }}>
      <Outlet />
    </CsrContext.Provider>
  );
}
